import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '../components/ui/command';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Calendar, Users, UserCheck, Printer, Bell, Network, ShieldCheck, Settings,
  Crown, Footprints, MapPin, ArrowLeftRight, Handshake, Plus, Search,
  User, Briefcase, Clock,
} from 'lucide-react';

const STATIC_ROUTES = [
  { id: 'r-dash', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, roles: ['administrator', 'supervisor', 'scheduler'] },
  { id: 'r-sched', label: 'Schedule', to: '/schedule', icon: Calendar, roles: ['administrator', 'supervisor', 'scheduler'] },
  { id: 'r-my', label: 'My Schedule', to: '/my-schedule', icon: Footprints, roles: '*' },
  { id: 'r-act', label: 'Activities', to: '/activities', icon: MapPin, roles: '*' },
  { id: 'r-meet', label: 'Meetings', to: '/meetings', icon: Handshake, roles: ['administrator', 'supervisor', 'scheduler'] },
  { id: 'r-staff', label: 'Staff', to: '/employees', icon: Users, roles: ['administrator', 'supervisor'] },
  { id: 'r-part', label: 'Participants', to: '/participants', icon: UserCheck, roles: ['administrator', 'supervisor'] },
  { id: 'r-print', label: 'Print / Export', to: '/print', icon: Printer, roles: ['administrator', 'supervisor', 'scheduler'] },
  { id: 'r-req', label: 'Requests', to: '/shift-requests', icon: ArrowLeftRight, roles: '*' },
  { id: 'r-notif', label: 'Notifications', to: '/notifications', icon: Bell, roles: '*' },
  { id: 'r-staffing', label: 'Staffing Hierarchy', to: '/staffing', icon: Network, roles: ['administrator'] },
  { id: 'r-pol', label: 'Policies', to: '/policies', icon: ShieldCheck, roles: '*' },
  { id: 'r-settings', label: 'Settings', to: '/settings', icon: Settings, roles: '*' },
  { id: 'r-ceo', label: 'CEO Life', to: '/ceo-life', icon: Crown, roles: ['*'], requireCeoLife: true },
];

const SETTINGS_TABS = [
  { id: 's-profile', label: 'Settings → My Profile', tab: 'profile' },
  { id: 's-team', label: 'Settings → Team', tab: 'accounts' },
  { id: 's-security', label: 'Settings → Security', tab: 'security' },
  { id: 's-services', label: 'Settings → Services', tab: 'service' },
  { id: 's-billing', label: 'Settings → Billing', tab: 'billing' },
  { id: 's-comms', label: 'Settings → Comms', tab: 'comms' },
  { id: 's-branding', label: 'Settings → Branding', tab: 'branding' },
  { id: 's-tools', label: 'Settings → Tools', tab: 'tools' },
  { id: 's-staff-access', label: 'Settings → Staff Access', tab: 'staff_access', adminOnly: true },
  { id: 's-audit', label: 'Settings → Audit Log', tab: 'audit_log', adminOnly: true },
];

const QUICK_ACTIONS = [
  { id: 'a-new-shift', label: 'New shift', hint: 'Open the schedule', to: '/schedule', icon: Plus, roles: ['administrator', 'supervisor', 'scheduler'] },
  { id: 'a-new-meeting', label: 'New meeting', hint: 'Schedule a meeting', to: '/meetings?new=1', icon: Plus, roles: ['administrator', 'supervisor', 'scheduler'] },
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [query, setQuery] = useState('');
  const [staff, setStaff] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [meetings, setMeetings] = useState([]);

  const role = user?.role || '';

  // Lazy-load search corpora when palette opens. Cache on the component instance —
  // a fresh fetch every open is fine for a small org and keeps things current.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const calls = [
          api.get('/employees').catch(() => ({ data: [] })),
          api.get('/participants').catch(() => ({ data: [] })),
        ];
        if (['administrator', 'supervisor', 'scheduler'].includes(role)) {
          calls.push(api.get('/meetings', { params: { scope: 'upcoming' } }).catch(() => ({ data: [] })));
        }
        const [s, p, m] = await Promise.all(calls);
        if (cancel) return;
        setStaff((s.data || []).filter(e => e.is_active !== false));
        setParticipants(p.data || []);
        setMeetings(m?.data || []);
      } catch { /* swallow */ }
    })();
    return () => { cancel = true; };
  }, [open, api, role]);

  const close = useCallback(() => { onOpenChange(false); setQuery(''); }, [onOpenChange]);

  const go = useCallback((to) => { navigate(to); close(); }, [navigate, close]);

  const goSettings = useCallback((tab) => {
    // Settings page reads ?tab=... if present; we also send hash for fallback.
    navigate(`/settings#${tab}`);
    // Soft retry after route mounts to set the active tab via custom event.
    setTimeout(() => window.dispatchEvent(new CustomEvent('cmdk:set-settings-tab', { detail: { tab } })), 50);
    close();
  }, [navigate, close]);

  const allowedRoutes = useMemo(() => STATIC_ROUTES.filter(r => {
    if (r.requireCeoLife && !user?.ceo_life_access) return false;
    if (r.roles === '*' || (Array.isArray(r.roles) && r.roles.includes('*'))) return true;
    return r.roles.includes(role);
  }), [role, user]);

  const allowedActions = useMemo(() => QUICK_ACTIONS.filter(a => a.roles === '*' || a.roles.includes(role)), [role]);

  const allowedSettings = useMemo(() => SETTINGS_TABS.filter(t => !t.adminOnly || role === 'administrator'), [role]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search staff, meetings, participants, pages…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No results for "{query}"</CommandEmpty>

        {allowedActions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {allowedActions.map(a => {
              const Icon = a.icon;
              return (
                <CommandItem key={a.id} value={`${a.label} ${a.hint}`} onSelect={() => go(a.to)} data-testid={`cmd-${a.id}`}>
                  <Icon className="mr-2 h-4 w-4 text-[#6B3FA0]" />
                  <span className="font-medium">{a.label}</span>
                  <span className="ml-2 text-xs text-[#8E85A0]">{a.hint}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Pages">
          {allowedRoutes.map(r => {
            const Icon = r.icon;
            return (
              <CommandItem key={r.id} value={`page ${r.label}`} onSelect={() => go(r.to)} data-testid={`cmd-${r.id}`}>
                <Icon className="mr-2 h-4 w-4 text-[#5A4D6E]" />
                {r.label}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Settings tabs">
          {allowedSettings.map(t => (
            <CommandItem key={t.id} value={`settings ${t.label}`} onSelect={() => goSettings(t.tab)} data-testid={`cmd-${t.id}`}>
              <Settings className="mr-2 h-4 w-4 text-[#5A4D6E]" />
              {t.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {staff.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Staff (${staff.length})`}>
              {staff.slice(0, 30).map(s => (
                <CommandItem key={`staff-${s.id}`} value={`staff ${s.name} ${s.email} ${s.role || ''}`}
                  onSelect={() => go(`/employees?focus=${s.id}`)}
                  data-testid={`cmd-staff-${s.id}`}>
                  <User className="mr-2 h-4 w-4 text-[#6B3FA0]" />
                  <span className="font-medium">{s.name}</span>
                  <span className="ml-2 text-xs text-[#8E85A0]">{s.email}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {participants.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Participants (${participants.length})`}>
              {participants.slice(0, 30).map(p => (
                <CommandItem key={`p-${p.id}`} value={`participant ${p.name}`}
                  onSelect={() => go(`/participants?focus=${p.id}`)}
                  data-testid={`cmd-participant-${p.id}`}>
                  <UserCheck className="mr-2 h-4 w-4 text-[#81C784]" />
                  {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {meetings.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Upcoming meetings (${meetings.length})`}>
              {meetings.slice(0, 20).map(m => (
                <CommandItem key={`m-${m.id}`} value={`meeting ${m.title} ${m.date}`}
                  onSelect={() => go(`/meetings`)}
                  data-testid={`cmd-meeting-${m.id}`}>
                  <Briefcase className="mr-2 h-4 w-4 text-[#FFB74D]" />
                  <span className="font-medium">{m.title}</span>
                  <span className="ml-2 text-xs text-[#8E85A0] flex items-center gap-1">
                    <Clock className="h-3 w-3" />{m.date} · {m.time_in}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

// Companion hook: registers the global ⌘K / Ctrl+K listener and exposes the toggle state.
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(prev => !prev);
      } else if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA' && !document.activeElement?.isContentEditable) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return [open, setOpen];
}
